import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";

import { Image } from "@/components/Image/Image";
import { PaymentDetails, PaymentDetailsRow } from "@/components/PaymentDetails";
import { TermsAndConditionsAlert } from "@/components/TermsAndConditionsAlert/TermsAndConditionsAlert";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { formatCurrency } from "@/utils/currency";

type Props = {
  basePrice: number;
  promoCodeDiscount?: number;
  summary: ReactNode;
};

export const PaymentSummaryCard = ({
  basePrice,
  promoCodeDiscount,
  summary,
}: Props) => {
  const intl = useIntl();
  const { selectedInstallments, paymentType } = usePaymentContext();

  const nextMonth = dayjs().add(1, "month");

  return (
    <PaymentDetails
      title={intl.formatMessage({
        defaultMessage: "Payment Summary",
        description: "Title for the payment summary section",
      })}
      summary={summary}
      footer={<TermsAndConditionsAlert />}
    >
      <PaymentDetailsRow
        label={intl.formatMessage({
          defaultMessage: "Hotel price",
          description:
            "Line item label for the base hotel price in the order summary",
        })}
        value={formatCurrency(basePrice)}
      />

      {promoCodeDiscount ? (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Promo code discount",
            description:
              "Line item label for the promo code discount in the price summary",
          })}
          value={formatCurrency(promoCodeDiscount, undefined, {
            prefix: "-",
          })}
          color="accent"
        />
      ) : null}

      {paymentType === "bnpl" ? (
        <>
          <PaymentDetailsRow
            label={intl.formatMessage({
              defaultMessage: "Next payment",
              description:
                "Label for the date of the next scheduled installment payment",
            })}
            value={intl.formatDate(nextMonth.toDate(), {
              year: "numeric",
              month: "short",
            })}
          />

          <PaymentDetailsRow
            label={intl.formatMessage({
              defaultMessage: "Number of installments",
              description:
                "Label for the number of installments in the payment plan",
            })}
            value={intl.formatMessage(
              {
                defaultMessage: "{count} months",
                description: "Number of months for the installment plan",
              },
              { count: selectedInstallments },
            )}
          />
        </>
      ) : (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Payment method",
            description:
              "Label for the selected payment method shown in the booking summary",
          })}
          value={
            <span className="inline-flex items-center gap-2">
              {intl.formatMessage({
                defaultMessage: "Qi Card",
                description: "Payment method label for the QI card",
              })}
              <Image
                src="/qicardlogo.png"
                alt=""
                aria-hidden
                className="shrink-0"
              />
            </span>
          }
        />
      )}
    </PaymentDetails>
  );
};
